import { DataGrid } from '@tableau/data-grid-1-wrapper';
import React from 'react';
import _ from 'underscore';

import { DisplaySettings } from '../../../../app/state/DisplaySettingsState';
import { ILocLinkifierFilter, ILocLinkifierModel } from '../../../../ts/filters/LocLinkifierFilter';
import { IEmptyTemplateModel, IEmptyTemplateParagraphs } from '../../../../ts/models/IEmptyTemplate';
import { ISliceable } from '../../../../ts/models/ISliceable';
import { Set } from '../../../../ts/models/Set';
import { Module } from '../../../../ts/Module';
import { Sorter } from '../../../../content/Sorter';
import { CommonStyles } from '../../CommonStyles';
import { SparseCachedList } from '../../SparseCachedList';

export interface IDataGridPropsScope<T> extends ng.IScope {
  dataGridProps: DataGrid.Props;
  displaySettings?: DisplaySettings;
  selectedItems?: Set<T>;
  sorter?: Sorter;
}

export interface AttachReactDataGridToScopeOptions<T> {
  $scope: IDataGridPropsScope<T>;
  columns: DataGrid.Column[];
  emptyChild: React.ReactElement<any>;
  canShowFilterPanel: boolean;
  headerHeight?: number;
  rowOuterHeight?: number;
  rememberLastScrollPos?: boolean;
}

const DefaultDataGridConfig: DataGrid.Config = {
  minimumColumnWidth: 38, // this is the smallest size we want the selection column to be
};

// TFS 769654 - When we upgrade to Data Grid 3, we'll need to use our own GridSelection object.
/*
export class VizPortalGridSelection<T> implements GridSelection {
    constructor(private itemsSet: Set<T>) { }

    public isSelected(cell: DataGrid.Cell) {
      return this.itemsSet.containsKey(cell.rowKey);
    }
}
*/

export class AngularToReactDataGridBridge {

  public static $inject = [
    '$translate',
    'locLinkifierFilter'
  ];

  constructor(
    private $translate: ng.translate.ITranslateService,
    private locLinkifier: ILocLinkifierFilter
  ) { }

  public attachReactDataGridToScope<T>(opts: AttachReactDataGridToScopeOptions<T>) {
    this.modifyDataGridProps(opts);
    this.addWatchersToScope(opts);
  }

  private modifyDataGridProps<T>(opts: AttachReactDataGridToScopeOptions<T>) {
    const { $scope, columns, emptyChild } = opts;

    const selectRow = (item: any, columnKey?: string) => {
      $scope.$apply(() => {
        if ($scope.selectedItems) {
          const onlySelectedRow = $scope.selectedItems.size() === 1 && $scope.selectedItems.contains(item);
          const isActionMenu = columnKey === 'actions';
          if (onlySelectedRow && !isActionMenu) {
            $scope.selectedItems.toggle(item);
          } else {
            $scope.selectedItems.clear();
            $scope.selectedItems.add(item);
          }

          // TFS 769654 - Use the new selection model when we upgrade to Data Grid 3.0
          // $scope.dataGridProps.selectionModel = new VizPortalGridSelection($scope.selectedItems);

          const keys = $scope.selectedItems.keys();
          $scope.dataGridProps.selectionData = <_.Dictionary<string>>_.object(keys, keys);
        }
      });
    };

    const configWithOptions: DataGrid.Config = {
      ...DefaultDataGridConfig,
      rowOuterHeight: opts.rowOuterHeight ? opts.rowOuterHeight : DefaultDataGridConfig.rowOuterHeight,
      rememberLastScrollPos: opts.rememberLastScrollPos
        ? opts.rememberLastScrollPos
        : DefaultDataGridConfig.rememberLastScrollPos
    };

    $scope.dataGridProps = {

      // Message to display for an empty grid
      emptyChild: emptyChild,

      // This gets incremented on each update, to force the data grid to re-render
      hackyUpdate: 0,

      cols: columns,

      leftMargin: this.leftMargin($scope),

      config: configWithOptions,

      headerHeight: opts.headerHeight? opts.headerHeight : null,

      // Sort on header click
      // TODO: (TFS 769651) Move asc/desc support completely out of the data grid when we upgrade to Data Grid 2.0
      onHeaderClicked: (columnKey: string, direction: string) => {
        $scope.$apply(() => {
          if ($scope.sorter) {
            $scope.sorter.setPrimary(columnKey, direction);
          }
        });
      },

      // Select row on cell click
      onCellClick: (item: any, columnKey: string) => {
        selectRow(item, columnKey);
      },

      // Select row on row click
      // TODO: When this prop is removed (when we upgrade the data grid), can we still select a row
      // by clicking in the blank area to the right of the last cell?
      onRowClick: (item: any) => {
        selectRow(item);
      },

      // TFS 769654 - Use the new selection model when we upgrade to Data Grid 3.0
      // selectionModel: new VizPortalGridSelection($scope.selectedItems)
    };
  }

  private addWatchersToScope<T>(opts: AttachReactDataGridToScopeOptions<T>) {
    const { $scope } = opts;

    // Re-render the grid when the sort order changes
    $scope.$watch(() => $scope.sorter && ($scope.sorter.selectedColumnKey() + ';' + $scope.sorter.isSortAscending()),
      () => {
        if ($scope.sorter) {
          $scope.dataGridProps.sortKey = $scope.sorter.selectedColumnKey();
          $scope.dataGridProps.sortAscending = $scope.sorter.isSortAscending();
        }
      });

    // Re-render the grid when the selection changes
    // TODO: replace this with a selectedItems store
    $scope.$watch(
      () => {
        if (!$scope.selectedItems || $scope.selectedItems.size() === 0) {
          return 'none';
        }

        return $scope.selectedItems.keys().join();
      },
      () => {
        if ($scope.selectedItems) {
          // TFS 769654 - Use the new selection model when we upgrade to Data Grid 3.0
          // $scope.dataGridProps.selectionModel = new VizPortalGridSelection($scope.selectedItems);
          const keys = $scope.selectedItems.keys();
          $scope.dataGridProps.selectionData = <_.Dictionary<string>>_.object(keys, keys);
        }
      });
  }

  public emptyTemplateElementFor(emptyTemplateModel: IEmptyTemplateModel) {

    const children: React.ReactElement<any>[] = [];

    if (emptyTemplateModel.title) {
      children.push(React.createElement('div', {
        key: 'title',
        className: 'tb-data-grid-empty-title'
      }, this.$translate.instant(emptyTemplateModel.title)));
    }

    if (emptyTemplateModel.body) {
      children.push(React.createElement('div', {
          key: 'body',
          className: 'tb-data-grid-empty-body'
        }, _.map(emptyTemplateModel.body, (paragraph: IEmptyTemplateParagraphs) => {
          return React.createElement('div', {
              className: 'tb-data-grid-empty-body-paragraph'
            }, _.map(paragraph.statements, (statement: ILocLinkifierModel) => {
              return React.createElement('div', {
                key: 'help',
                dangerouslySetInnerHTML: {__html: this.locLinkifier(statement)}
              });
            })
          );
        }))
      );
    }

    if (emptyTemplateModel.help) {
      children.push(React.createElement('div', {
        key: 'help',
        className: 'tb-data-grid-empty-help-link',
        dangerouslySetInnerHTML: { __html: this.locLinkifier(emptyTemplateModel.help) }
      }));
    }

    return React.createElement('div', { className: 'tb-data-grid-empty' }, children);
  }

  public emptyTemplateElementForNoAssignedSites(emptyTemplateModel: IEmptyTemplateModel) {

    const children: React.ReactElement<any>[] = [];

    children.push(React.createElement('div', {
        key: 'title',
        className: 'tb-data-grid-no-matching-results'
      }, this.$translate.instant(emptyTemplateModel.title)));

    children.push(React.createElement('a', {
        key: 'help',
        onClick: emptyTemplateModel.action
    }, this.$translate.instant('assignSites')));

    return React.createElement('div', { className: 'tb-site-membership-grid-empty' }, children);
  }

  private leftMargin<T>($scope: IDataGridPropsScope<T>) {
    // Data grids in content subplaces have left margins, but the others do not
    // (login site picker, revision history, content action notification).
    //
    // Checking for the presence of displaySettings is a pretty icky way to figure this out:
    // it happens to work because the non-subplace data grids are all within stacked elements, toasts,
    // or the login state, which are outside of the "main" div that adds displaySettings to the
    // inherited scope.
    //
    // DisplaySettings used to matter because the left margin changed depending on the presence
    // of the filter panel, but since the filter panel moved to the right, it's no longer necessary.
    // This could be refactored to pass in a "leftMargin" option to the data grid bridge, set
    // appropriately by each controller.
    return $scope.displaySettings ? CommonStyles.Sizes.MediumSpace : 0;
  }

  public updateReactDataGridRowStore<T>(
    config: {
      scope: IDataGridPropsScope<T>;
      sliceable: ISliceable<any>;
      getRowStyle?: (item: T) => any;
      keyForItem?: (item: T) => string;
    }) {

    const keyForItem = config.keyForItem || ((item: T) => (<any>item)['id']);

    const sparseList = new SparseCachedList(
      config.sliceable,
      (item: T): DataGrid.Row => {
        return {
          key: keyForItem(item),
          data: item as any,
          // TODO: 769624 - Replace rowStyle with specifically styled cell formatters when we upgrade to Data Grid 2.0
          rowStyle: config.getRowStyle ? config.getRowStyle(item) : null
        };
      });

    // map the Q API to a generic callback
    config.scope.dataGridProps.rowStore = {
      getItems: (start: number, end: number, rowHandler: (rows: DataGrid.Row[]) => void) => {
        sparseList.getItems(start, end).then(rowHandler);
      }
    };
  }

  public static getReactDataGridRowStore<T>(sliceable: ISliceable<any>) {

    const keyForItem = ((item: T) => (<any>item)['id']);

    const sparseList = new SparseCachedList(
      sliceable,
      (item: T): DataGrid.Row => {
        return {
          key: keyForItem(item),
          data: item as any,
          // TODO: 769624 - Replace rowStyle with specifically styled cell formatters when we upgrade to Data Grid 2.0
          rowStyle: null
        };
      });

    // map the Q API to a generic callback
    return {
      getItems: (start: number, end: number, rowHandler: (rows: DataGrid.Row[]) => void) => {
        sparseList.getItems(start, end).then(rowHandler);
      }
    };
  }

}

Module.instance().service('AngularToReactDataGridBridge', AngularToReactDataGridBridge);
